import type { AppVersionPolicyResponse } from '@/api/gen-new/model';
import type { NativeAppInfo } from './nativeAppInfo';

import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { isAxiosError } from 'axios';

import { appVersionGetVersionPolicy } from '@/api/gen-new/app/app';
import { AXIOS_INSTANCE } from '@/api/mutator/custom-instance-new';

import { getNativeAppInfo } from './nativeAppInfo';

/** What the API answers once the minimum version has risen past this build. */
const HTTP_UPGRADE_REQUIRED = 426;

export type AppUpdateState = {
	appInfo: NativeAppInfo | null;
	policy: AppVersionPolicyResponse | null;
	/** Set by a 426, which is the server's own verdict and blocks even if the policy fetch fails. */
	upgradeRequired: boolean;
};

/**
 * Lives outside the React Query cache on purpose: every sign-out clears that cache, and clearing
 * it cancelled the policy query in flight, so a 426 on a failed sign-in never showed the gate.
 */
let state: AppUpdateState = { appInfo: null, policy: null, upgradeRequired: false };
const listeners = new Set<() => void>();
let inFlight: Promise<void> | undefined;

function setState(patch: Partial<AppUpdateState>): void {
	state = { ...state, ...patch };
	listeners.forEach(listener => listener());
}

export function subscribeToAppUpdate(listener: () => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

export function getAppUpdateState(): AppUpdateState {
	return state;
}

/** Concurrent callers share one request. A failed check keeps the last answer and never blocks. */
export function refreshVersionPolicy(): Promise<void> {
	inFlight ??= loadVersionPolicy().finally(() => {
		inFlight = undefined;
	});
	return inFlight;
}

async function loadVersionPolicy(): Promise<void> {
	const appInfo = await getNativeAppInfo();
	if (!appInfo) {
		return;
	}

	try {
		const policy = await appVersionGetVersionPolicy({ platform: appInfo.platform });
		setState({ appInfo, policy });
	} catch {
		setState({ appInfo });
	}
}

/** Checks at launch and on every return to the foreground, and blocks as soon as a request gets 426. */
export function installAppUpdateCheck(): void {
	if (!Capacitor.isNativePlatform()) {
		return;
	}

	void refreshVersionPolicy();

	void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
		if (isActive) {
			void refreshVersionPolicy();
		}
	}).catch(() => undefined);

	AXIOS_INSTANCE.interceptors.response.use(undefined, (error: unknown) => {
		if (isAxiosError(error) && error.response?.status === HTTP_UPGRADE_REQUIRED && !state.upgradeRequired) {
			setState({ upgradeRequired: true });
			// For the store link; the block itself does not wait for it.
			void refreshVersionPolicy();
		}
		return Promise.reject(error);
	});
}
